import { useEffect, useState } from "react";
import { SECTIONS } from "../components/managed-docs-content.js";
import { go, SaasFooter, SaasNav } from "../components/saas-shell.js";

/**
 * Documentation for the managed databases, on its own page rather than folded
 * into the file-sharing docs.
 *
 * The two products share an account and nothing else: someone here wants
 * ingest, SQL and endpoints, and would have to scroll past `saucer send` to
 * find any of it. Keeping them apart also means neither page has to hedge about
 * which product a sentence is describing.
 *
 * The prose lives in `components/managed-docs-content.tsx` because the
 * workspace renders the same sections inline. This file is the public shell
 * around them: nav, rail, footer.
 */
export function ManagedDocs() {
  const [active, setActive] = useState<string>("start");
  const sections = SECTIONS;

  // Highlights whichever heading is nearest the top of the viewport, so the
  // rail tracks reading position rather than only clicks.
  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        const visible = entries
          .filter((entry) => entry.isIntersecting)
          .sort((a, b) => a.boundingClientRect.top - b.boundingClientRect.top)[0];
        if (visible) setActive(visible.target.id);
      },
      { rootMargin: "-80px 0px -70% 0px", threshold: 0 },
    );

    for (const section of sections) {
      const node = document.getElementById(section.id);
      if (node) observer.observe(node);
    }
    return () => observer.disconnect();
  }, [sections]);

  return (
    <div className="landing saas">
      <SaasNav />

      <div className="mdocs saas-wrap-5xl">
        <aside className="mdocs__rail">
          <span className="mdocs__rail-head">Managed databases</span>
          <nav>
            {sections.map((section) => (
              <a
                key={section.id}
                href={`#${section.id}`}
                className={`mdocs__link${active === section.id ? " mdocs__link--on" : ""}`}
                onClick={(event) => {
                  event.preventDefault();
                  document.getElementById(section.id)?.scrollIntoView({ behavior: "smooth" });
                  setActive(section.id);
                }}
              >
                {section.title}
              </a>
            ))}
          </nav>
          <a className="mdocs__back" href="/managed" onClick={go("/managed")}>
            ← Product overview
          </a>
        </aside>

        <main className="mdocs__body">
          <header className="mdocs__masthead">
            <span className="saas-chip">Docs</span>
            <h1 className="mdocs__title">Managed ClickHouse &amp; Postgres</h1>
            <p className="saas-lede">
              Everything from an empty account to a published endpoint. The API is deliberately
              close to Tinybird's, so an existing integration is mostly a hostname change.
            </p>
          </header>

          {sections.map((section) => (
            <section key={section.id} id={section.id} className="mdocs__section">
              <h2 className="mdocs__h2">{section.title}</h2>
              {section.body}
            </section>
          ))}
        </main>
      </div>

      <SaasFooter />
    </div>
  );
}
